/**
 * NTP-style server-clock offset estimation for multi-room sync.
 *
 * Devices trust their local wall-clock when following the server-issued
 * segment timeline; any skew becomes audible drift between rooms. Clients
 * sample the server clock over a few round-trips and correct with the offset
 * from the minimum-RTT sample (least queueing noise), re-sampling every few
 * minutes. Pure: no Date.now() here — callers pass timestamps.
 */

export interface ClockSample {
  /** Client wall-clock when the request left (ms). */
  sentAtMs: number;
  /** Server wall-clock from the response body (ms). */
  serverTimeMs: number;
  /** Client wall-clock when the response arrived (ms). */
  receivedAtMs: number;
}

export interface ClockOffsetEstimate {
  /** Add to the client clock to approximate the server clock (may be negative). */
  offsetMs: number;
  /** Round-trip time of the sample the estimate came from. */
  rttMs: number;
  sampleCount: number;
}

/** Recommended number of round-trips per estimation burst. */
export const CLOCK_SAMPLE_COUNT = 5;
/** Recommended re-estimation interval. */
export const CLOCK_RESAMPLE_INTERVAL_MS = 5 * 60_000;
/**
 * Samples with an RTT above this carry too much queueing noise to bound the
 * offset usefully; a burst where every sample exceeds it yields no estimate.
 */
export const CLOCK_MAX_USABLE_RTT_MS = 2_000;

/**
 * Estimate the client→server clock offset from round-trip samples.
 *
 * For a sample, the server read its clock somewhere between `sentAtMs` and
 * `receivedAtMs`; assuming symmetric network delay, the midpoint estimate is
 * `offset = serverTimeMs - (sentAtMs + rtt / 2)`, with error bounded by
 * ±rtt/2 — hence picking the minimum-RTT sample.
 *
 * Returns null when no usable sample exists (empty, malformed, or all above
 * CLOCK_MAX_USABLE_RTT_MS).
 */
export function estimateClockOffset(
  samples: readonly ClockSample[],
): ClockOffsetEstimate | null {
  let best: { offsetMs: number; rttMs: number } | null = null;
  let usable = 0;
  for (const s of samples) {
    const rttMs = s.receivedAtMs - s.sentAtMs;
    if (
      !Number.isFinite(rttMs) ||
      !Number.isFinite(s.serverTimeMs) ||
      rttMs < 0 ||
      rttMs > CLOCK_MAX_USABLE_RTT_MS
    ) {
      continue;
    }
    usable++;
    const offsetMs = s.serverTimeMs - (s.sentAtMs + rttMs / 2);
    if (!best || rttMs < best.rttMs) {
      best = { offsetMs, rttMs };
    }
  }
  if (!best) return null;
  return { offsetMs: best.offsetMs, rttMs: best.rttMs, sampleCount: usable };
}

/** Server-clock approximation for a given client wall-clock reading. */
export function correctedNow(offsetMs: number, clientNowMs: number): number {
  return clientNowMs + (Number.isFinite(offsetMs) ? offsetMs : 0);
}
